<script setup lang="ts">
import { ref, watch } from 'vue';
import { loadGroupChildrenInternal } from '@/api/InternalGroupApi';
import type { Group } from '@/models/Group';
import type { GroupChildren } from '@/models/GroupChildren';
import RobotCardInternal from '@/components/internalFleet/RobotCardInternal.vue';

const props = defineProps<{ userUuid: string; group: Group }>();

const loading = ref(false);
const groupChildren = ref<GroupChildren | null>(null);

const reloadChildren = async () => {
  loading.value = true;
  try {
    groupChildren.value = await loadGroupChildrenInternal(
      props.userUuid,
      props.group.uuid,
    );
  } catch (error) {
    console.error('Failed to fetch group children:', error);
  } finally {
    loading.value = false;
  }
};

const fetchChildren = async () => {
  if (loading.value || groupChildren.value) {
    return;
  }
  await reloadChildren();
};

watch([() => props.userUuid, () => props.group.uuid], fetchChildren, {
  immediate: true,
});
</script>

<template>
  <v-expansion-panel>
    <v-expansion-panel-title style="height: 48px" @click="fetchChildren">
      <span class="m-2 font-weight-bold">{{ props.group.name }}</span>
      <v-progress-circular
        v-if="loading"
        indeterminate
        color="primary"
        size="24"
        class="ml-2"
      ></v-progress-circular>
    </v-expansion-panel-title>
    <v-expansion-panel-text>
      <div v-if="loading" class="empty-group-message">
        <span>{{ $gettext('Loading') }}...</span>
      </div>
      <div
        v-else-if="
          !groupChildren?.groups.length && !groupChildren?.controlUnits.length
        "
        class="empty-group-message"
      >
        <span>{{ $gettext('Empty Group') }}</span>
      </div>
      <div v-else class="mb-6">
        <v-expansion-panels v-if="groupChildren?.groups.length" multiple>
          <template
            v-for="subGroup in groupChildren.groups"
            :key="subGroup.uuid"
          >
            <GroupPanelInternal :userUuid="props.userUuid" :group="subGroup" />
          </template>
        </v-expansion-panels>
        <v-row>
          <v-col
            v-for="robot in groupChildren?.controlUnits"
            :key="robot.serialNumber"
            cols="12"
            md="6"
            lg="4"
            xl="3"
            xxl="2"
            class="mt-4"
          >
            <RobotCardInternal :robot="robot" />
          </v-col>
        </v-row>
      </div>
    </v-expansion-panel-text>
  </v-expansion-panel>
</template>

<style scoped>
.empty-group-message {
  text-align: center;
  font-style: italic;
  color: var(--v-theme-on-background);
}
</style>
